import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { searchHandler } from "@pineseed/gui/handler/search-handler";
import type {
  SearchState,
  SearchResult,
  SearchResultSection,
  SearcherRegistration,
} from "@pineseed/gui/types";

export interface SearchContextValue {
  searchState: SearchState;
  startSearch: () => void;
  stopSearch: () => void;
  search: (query: string) => void;
  executeSearchResult: (result: SearchResult) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  navigateLeft: () => void;
  navigateRight: () => void;
  getCurrentSelection: () => {
    section: SearchResultSection;
    result: SearchResult;
    sectionIndex: number;
    resultIndex: number;
  } | null;
  executeCurrentSelection: () => void;
  searcherConfigs: SearcherRegistration[];
  enableSearcher: (type: string) => void;
  disableSearcher: (type: string) => void;
  registerSearcher: (registration: SearcherRegistration) => void;
  unregisterSearcher: (type: string) => void;
}

const INITIAL_SEARCH_STATE: SearchState = {
  isOpen: false,
  query: "",
  sections: [],
  selectedSectionIndex: 0,
  selectedResultIndex: 0,
  isSearching: false,
};

const SearchContext = createContext<SearchContextValue | null>(null);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [searchState, setSearchState] =
    useState<SearchState>(INITIAL_SEARCH_STATE);
  const [searcherConfigs, setSearcherConfigs] = useState<
    SearcherRegistration[]
  >(() => searchHandler.getRegisteredSearchers());

  const startSearch = useCallback(() => {
    setSearchState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const stopSearch = useCallback(() => {
    setSearchState(INITIAL_SEARCH_STATE);
  }, []);

  const search = useCallback((query: string) => {
    setSearchState((prev) => ({ ...prev, query }));

    const setLoading = (isSearching: boolean) => {
      setSearchState((prev) => ({ ...prev, isSearching }));
    };
    const setSections = (sections: SearchResultSection[]) => {
      setSearchState((prev) => ({
        ...prev,
        sections,
        selectedSectionIndex: 0,
        selectedResultIndex: 0,
      }));
    };

    searchHandler.performSearch(query, setLoading, setSections);
  }, []);

  const executeSearchResult = useCallback((result: SearchResult) => {
    searchHandler.executeResult(result);
  }, []);

  const navigateSelection = useCallback(
    (direction: "up" | "down" | "left" | "right") => {
      setSearchState((prev) => {
        const { sections, selectedSectionIndex, selectedResultIndex } = prev;

        if (sections.length === 0) return prev;

        let newSectionIndex = selectedSectionIndex;
        let newResultIndex = selectedResultIndex;

        if (direction === "up") {
          if (newResultIndex > 0) {
            newResultIndex--;
          } else if (newSectionIndex > 0) {
            newSectionIndex--;
            newResultIndex = Math.max(
              0,
              sections[newSectionIndex].results.length - 1,
            );
          }
        } else if (direction === "down") {
          const currentSection = sections[newSectionIndex];
          if (newResultIndex < currentSection.results.length - 1) {
            newResultIndex++;
          } else if (newSectionIndex < sections.length - 1) {
            newSectionIndex++;
            newResultIndex = 0;
          }
        }

        return {
          ...prev,
          selectedSectionIndex: newSectionIndex,
          selectedResultIndex: newResultIndex,
        };
      });
    },
    [],
  );

  const navigateUp = useCallback(
    () => navigateSelection("up"),
    [navigateSelection],
  );
  const navigateDown = useCallback(
    () => navigateSelection("down"),
    [navigateSelection],
  );
  const navigateLeft = useCallback(
    () => navigateSelection("left"),
    [navigateSelection],
  );
  const navigateRight = useCallback(
    () => navigateSelection("right"),
    [navigateSelection],
  );

  const getCurrentSelection = useCallback(() => {
    const { sections, selectedSectionIndex, selectedResultIndex } = searchState;
    if (sections.length === 0) return null;
    const section = sections[selectedSectionIndex];
    if (!section || section.results.length === 0) return null;
    const result = section.results[selectedResultIndex];
    if (!result) return null;
    return {
      section,
      result,
      sectionIndex: selectedSectionIndex,
      resultIndex: selectedResultIndex,
    };
  }, [searchState]);

  const executeCurrentSelection = useCallback(() => {
    const selection = getCurrentSelection();
    if (selection) {
      searchHandler.executeResult(selection.result);
    }
  }, [getCurrentSelection]);

  const refreshSearcherConfigs = useCallback(() => {
    setSearcherConfigs(searchHandler.getRegisteredSearchers());
  }, []);

  const enableSearcher = useCallback(
    (type: string) => {
      searchHandler.setSearcherEnabled(type, true);
      refreshSearcherConfigs();
    },
    [refreshSearcherConfigs],
  );

  const disableSearcher = useCallback(
    (type: string) => {
      searchHandler.setSearcherEnabled(type, false);
      refreshSearcherConfigs();
    },
    [refreshSearcherConfigs],
  );

  const registerSearcher = useCallback(
    (registration: SearcherRegistration) => {
      searchHandler.registerSearcher(registration);
      refreshSearcherConfigs();
    },
    [refreshSearcherConfigs],
  );

  const unregisterSearcher = useCallback(
    (type: string) => {
      searchHandler.unregisterSearcher(type);
      refreshSearcherConfigs();
    },
    [refreshSearcherConfigs],
  );

  const value = useMemo<SearchContextValue>(
    () => ({
      searchState,
      startSearch,
      stopSearch,
      search,
      executeSearchResult,
      navigateUp,
      navigateDown,
      navigateLeft,
      navigateRight,
      getCurrentSelection,
      executeCurrentSelection,
      searcherConfigs,
      enableSearcher,
      disableSearcher,
      registerSearcher,
      unregisterSearcher,
    }),
    [
      searchState,
      startSearch,
      stopSearch,
      search,
      executeSearchResult,
      navigateUp,
      navigateDown,
      navigateLeft,
      navigateRight,
      getCurrentSelection,
      executeCurrentSelection,
      searcherConfigs,
      enableSearcher,
      disableSearcher,
      registerSearcher,
      unregisterSearcher,
    ],
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useSearchContext(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (ctx === null) {
    throw new Error(
      "useSearchContext must be used within a SearchProvider",
    );
  }
  return ctx;
}
